import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './pagination'
import React from 'react'

interface PaginationControlsProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
}

const ELLIPSIS = 'ellipsis'
const SIBLING_COUNT = 1

const range = (start: number, end: number): number[] => Array.from({ length: end - start + 1 }, (_, i) => start + i)

/** First & last always shown, current ±SIBLING_COUNT, ellipsis where collapsed; all pages when they fit. */
const getPageItems = (page: number, totalPages: number): Array<number | typeof ELLIPSIS> => {
  const totalToShow = SIBLING_COUNT * 2 + 5
  if (totalPages <= totalToShow) return range(1, totalPages)

  const leftSibling = Math.max(page - SIBLING_COUNT, 1)
  const rightSibling = Math.min(page + SIBLING_COUNT, totalPages)
  const showLeftDots = leftSibling > 2
  const showRightDots = rightSibling < totalPages - 1

  if (!showLeftDots && showRightDots) return [...range(1, 3 + SIBLING_COUNT * 2), ELLIPSIS, totalPages]
  if (showLeftDots && !showRightDots) return [1, ELLIPSIS, ...range(totalPages - (2 + SIBLING_COUNT * 2), totalPages)]
  return [1, ELLIPSIS, ...range(leftSibling, rightSibling), ELLIPSIS, totalPages]
}

/** Numbered + prev/next pagination built on the shadcn primitives. Page size is owned by the caller. */
export const PaginationControls = ({
  page,
  totalPages,
  onPageChange,
  className,
}: PaginationControlsProps): React.ReactElement | null => {
  if (totalPages <= 1) return null

  const items = getPageItems(page, totalPages)
  const isFirst = page <= 1
  const isLast = page >= totalPages

  return (
    <Pagination className={className}>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious disabled={isFirst} onClick={() => onPageChange(page - 1)} />
        </PaginationItem>
        {items.map((item, index) =>
          item === ELLIPSIS ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={item}>
              <PaginationLink isActive={item === page} onClick={() => onPageChange(item)}>
                {item}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext disabled={isLast} onClick={() => onPageChange(page + 1)} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}
